import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Components
import CarTopLabel from '~/components/CarTopLabel';
const Button = dynamic(() => import('~/components/Button'), {
  ssr: false,
});
const WishlistButton = dynamic(() => import('~/components/WishlistButton'), {
  ssr: false,
});
import TooltipInfo from '~/components/TooltipInfo';
import CarImagesKitCarousel from '~/components/CarImagesKitCarousel/';
import { COLORS } from '~/data/ui/colors';

// Data
import RESOURCES from '~/resources';
import {
  CAR_FLOW_TYPE,
  CAR_DEAL_TYPE_OPTIONS,
  getLegalInfoPerDealType,
} from '~/data/car/carCardData';
import { CAR_TARGET } from '~/data/consts';
import { BUTTON_SIZES, BUTTON_TYPES } from '~/data/constants';
import {
  CarCardProps,
  getLicenseFeePriceDetails,
  getPurchasingDetails,
} from '~/data/types/car/cars';
import { CarAcquireType } from '~/data/car/carSubscriptionData';
import { imageKitThumb } from '~/data/imageKitData';
// Utils
import { generateSeed, getRandomIndices } from '~/utils/getRandomIndices';
import { sortLabelsByRank } from '~/utils/cleanObject';
import { useIsCarZeroKm } from '~/hooks/useIsCarZeroKm';
import ZeroKmIcon from '~/assets/icons/jsx/zeroKMicon';
import ZeroKMBackground from '~/assets/icons/jsx/ZeroKMBackground';
import { formatPrice } from '~/hooks/useFormatPrice';
import { useImagesKitS3 } from '~/hooks/useImageKitS3';
import { useInitialCarDealOption } from '~/hooks/useInitialCarDealOption';
import { EActionEvent, ECategoryEvent, useEvents } from '~/hooks/useEvents';
import { useContactUsPopup } from '~/hooks/useContactUsPopup';
// In Hook
import { useCarPageUrl } from './useCarPageUrl';
import { useCarSubscriptionPageUrl } from './useCarSubscriptionPageUrl';
// Store
import { setCarType } from '~/redux-toolkit/slices/carSlice';
// Styles
import useStyles, {
  bottomContainer,
  bottomPriceContainer,
  brandNameStyle,
  StyledBulletIcon,
  bulletTextStyles,
  bulletsContainer,
  buttonStyle,
  contactUsButton,
  contactUsButtonText,
  contentContainer,
  customWishlistStyles,
  hrefBtnStyle,
  modelNameStyle,
  priceListCarCardStyle,
  priceStyle,
  tooltipCustomStyles,
  topLabelStyles,
  yellowLineContainerStyles,
  yellowLineStyles,
  yellowLineLeaseContainerStyles,
  iconCampaign,
  frameCampaign,
  timerText,
  timerStyle,
  priceText,
  beforeDiscountText,
  beforeDiscount,
  CarPromotionDoodleDiamondStyled,
  CarPromotionDoodleAsteriskStyled,
  CarPromotionDoodleStarStyled,
  promoRoot,
  CarPromotionDoodleDiamondTightStyled,
  CarPromotionDoodleAsteriskTightStyled,
  CarPromotionDoodleStarTightStyled,
  PriceContentContainer,
  zeroKmStyle,
} from './useStyles';
import { CarImagesCarouselStyle } from '../UsedCarCard/useStyles';
// Icons
import dynamic from 'next/dynamic';
import TimerIcon from '~/assets/icons/jsx/timerIcon';
import { useAppDispatch, useAppSelector } from '~/redux-toolkit/store/hooks';
import { RootState } from '~/redux-toolkit/store/store';
import useStringTemplate from '~/hooks/useStringTemplate';
import { CONTACT_US_REDIRECTION } from '~/data/consts';
import { ESourceEventOptions } from '~/data/lead/enums';
import { EContactUsFlow } from '~/data/ContactUsPopup/enums';
import { getCampaignNumberByCar } from '~/data/lead/campaignNumber';
import { subMedia } from '~/data/lead/subMedia';
import Link from 'next/link';
import useLeadNewResultPageCarSelected from '~/hooks/useLeadNewResultPageCarSelected';
import BulletCard from '~/assets/icons/jsx/BulletCard';
import { useIsLeaseCar } from '~/hooks/useIsLeaseCar';
import YellowLineTitle from '~/assets/icons/jsx/yellowLineTitle';
import { FONT_SIZES } from '~/data/ui/fontSizes.data';
import useLineOfBusinessTarget from '~/hooks/useLineOfBusinessTarget';
import { useCarsPagesLeadLocation } from '~/hooks/useCarsPagesLeadLocation';
import { useLeadEventDetails } from '~/hooks/useLeadEventDetails';
import { priceStyles } from '../LobbyBudget/useStyles';
import { useRouter } from 'next/router';
import { ROUTES } from '~/data/routes';
import Frame from '~/components/Frame/frame';
import { calculateTimeLeft } from '~/utils/timeUtils';
import useCarCampaigns from '~/hooks/useCarCampaigns';
import { useCarDetailsNote } from '~/hooks/useCarDetailsNote';
import CarPromotionDoodleAsterisk from '~/assets/icons/jsx/carPromotionDoodleAsterisk';
import CarPromotionDoodleStar from '~/assets/icons/jsx/carPromotionDoodleStar';
import CarPromotionDoodleDiamond from '~/assets/icons/jsx/carPromotionDoodleDiamond';
import {
  appendToCookieIfNotExists,
  isValueInCookie,
} from '~/utils/cookieStorageHandlers';
import { parseMarkdownBold } from '~/utils/parseTextToBold';
import { ARIA_LABELS_RESOURCES } from '~/resources/resources';
import Image from 'next/image';
import carOfTheYear from '~/assets/icons/carOfTheYear.svg';
import { useCarOfTheYear } from '~/hooks/useCarOfTheYear';

const FomoLabel = dynamic(() => import('../FomoLabel/FomoLabel'), {
  ssr: false,
});
const CarCard: FC<CarCardProps> = ({
  car,
  isInWishlist,
  onWishlistButtonClicked,
}) => {
  const events = useEvents();
  const router = useRouter();
  const dispatch = useAppDispatch();

  const isPrivateLeasing = useIsLeaseCar();
  const carFlowDefaultValue = useInitialCarDealOption(car);
  const formatString = useStringTemplate();
  const { getCarDetailsNote } = useCarDetailsNote();
  const { leadLocationOption } = useCarsPagesLeadLocation();
  const [timeLeft, setTimeLeft] = useState<{
    hours: string;
    minutes: string;
    seconds: string;
    days?: string;
  } | null>(null);

  const promotionCampaignCards = useAppSelector(
    (state: RootState) => state.resultPage.promotionCampaignCards,
  );

  const { setSelectedCar } = useLeadNewResultPageCarSelected();
  const images: (string | null)[] = useImagesKitS3(car?.images || []);
  const businessLineTarget = useLineOfBusinessTarget();
  const isCarZeroKm = useIsCarZeroKm(car);

  const { isCarOfTheYear } = useCarOfTheYear(car.id);

  const businessLine = useMemo(() => {
    if (!businessLineTarget) {
      if (car.newCarMinPrice === 0) {
        return CAR_TARGET.LEASING;
      } else {
        return CAR_TARGET.NEW_CAR;
      }
    }
    return businessLineTarget;
  }, [businessLineTarget, car.newCarMinPrice]);

  const mappedCampaignCards = useMemo(() => {
    return (promotionCampaignCards || []).map((campaign) => ({
      ...campaign,
      icon: campaign.icon?.url,
    }));
  }, [promotionCampaignCards]);

  const page = isPrivateLeasing ? 'privateLeasing' : 'newCar';

  const {
    campaign,
    isPromoted,
    isInRangeDate,
    isTimerDisplayed,
    promotionIconUrl,
  } = useCarCampaigns(car, mappedCampaignCards, page);

  useEffect(() => {
    if (
      isInRangeDate &&
      campaign?.showTimer &&
      isPromoted &&
      campaign?.endDate
    ) {
      const endDate = new Date(campaign.endDate);
      const updateTimeLeft = () => {
        const remainingTime = calculateTimeLeft(endDate);
        setTimeLeft(remainingTime);
      };

      updateTimeLeft();
      const timer = setInterval(updateTimeLeft, 1000);

      return () => clearInterval(timer);
    }
  }, [isPromoted, campaign?.showTimer, campaign?.endDate, isInRangeDate]);

  const isWishListPage = router.pathname === ROUTES.WISHLIST_PAGE;

  const isPrivateLeasingInWishPage = useMemo(() => {
    if (!isWishListPage) return false;
    if (!car.newCarMinPrice) return true;
    return false;
  }, [car.newCarMinPrice, isWishListPage]);

  const topLabel = useMemo(() => {
    if (car.carLabels && car.carLabels.data.length > 0) {
      const sortedLabels = sortLabelsByRank(
        car.carLabels.data.filter(
          (label) => !label.isPrioritized && label.type !== 'promotion',
        ),
      );
      return (sortedLabels && sortedLabels[0]?.title) ?? '';
    }

    if (car.carLabels && car.carLabels.data.length > 0) {
      const seed = generateSeed(`${car.id}`, new Date());
      const randomIndices = getRandomIndices(
        car.carLabels.data.length,
        1,
        seed,
      );
      return car.carLabels.data[randomIndices[0]].title;
    }

    if (car.carCardBottomLabel) {
      return car.carCardBottomLabel;
    }

    return '';
  }, [car]);

  const [selectedButton, setSelectedButton] = useState(carFlowDefaultValue);

  const { primeRate, newCarTooltipDesc } = useAppSelector(
    (state: RootState) => state.funding,
  );

  useEffect(() => {
    if (carFlowDefaultValue !== selectedButton) {
      setSelectedButton(carFlowDefaultValue);
    }
  }, [setSelectedButton, carFlowDefaultValue, selectedButton]);

  const shouldShowFomo = useMemo(() => {
    if (!isPrivateLeasing) return false;

    const viewThreshold = 700;
    return car.pageViewsOneMonthAgo > viewThreshold;
  }, [car.pageViewsOneMonthAgo, isPrivateLeasing]);

  const onWishlistIconClicked = useCallback(() => {
    const action = isInWishlist
      ? EActionEvent.REMOVE_WISHLIST
      : EActionEvent.ADD_TO_WISHLIST;
    events.sendEvent(
      {
        action,
        category: ECategoryEvent.USER,
      },
      {
        businessLine: businessLine,
        carMake: car.dmvManufacturerName,
        carModel: car.modelNameDisplayName || car.dmvModelName,
        carYear: car.year,
      },
    );
    onWishlistButtonClicked(car.id);
  }, [car, events, isInWishlist, onWishlistButtonClicked, businessLine]);

  const price: number = useMemo(() => {
    return isPrivateLeasing || isPrivateLeasingInWishPage
      ? car.leaseMinPrice
      : car.newCarMinPrice;
  }, [car, isPrivateLeasing, isPrivateLeasingInWishPage]);

  const priceRef = useRef<HTMLDivElement>(null);

  const { openContactUsModal } = useContactUsPopup();

  const classes = useStyles();

  const onClickSubmitButton = useCallback(() => {
    if (car.promotion && car.promotion.isPublish) {
      appendToCookieIfNotExists('carVisited', car.id, 356);
    }

    events.sendEvent(
      {
        category: ECategoryEvent.RESULT_PAGE,
        action: EActionEvent.CAR_RESULTS_PAGE,
      },
      {
        carMake: car?.dmvManufacturerName,
        carModel: car?.modelNameDisplayName || car?.dmvModelName,
        dealType: selectedButton,
        carYear: car.year,
        businessLine: businessLine,
        topLabel,
        gaLabel: shouldShowFomo,
      },
    );
    // dispatch(setCarType(selectedButton));
  }, [
    selectedButton,
    // dispatch,
    events,
    car,
    businessLine,
    topLabel,
    shouldShowFomo,
  ]);

  const legalInfo = useCallback(
    (flowType: CAR_FLOW_TYPE) => {
      if (isCarZeroKm) {
        return RESOURCES.TOOLTIP_ZERO;
      }
      switch (flowType) {
        case CAR_FLOW_TYPE.LEASING_CAR:
          // eslint-disable-next-line no-case-declarations
          const { periodInMonths = '', advance = 0 } =
            getPurchasingDetails(price, car?.originalLeaseCarPrice) || {};
          if (periodInMonths && advance) {
            return `${RESOURCES.CAR_LEASE_TOOLTIP.replace(
              '{period}',
              periodInMonths?.toString(),
            ).replace('{advance}', advance?.toLocaleString())}`;
          }
          return getLegalInfoPerDealType(CAR_FLOW_TYPE.LEASING_CAR);
        case CAR_FLOW_TYPE.LOAN_CAR:
          return formatString(newCarTooltipDesc, {
            primeRate,
          });
        case CAR_FLOW_TYPE.NEW_CAR:
          return getLegalInfoPerDealType(
            CAR_FLOW_TYPE.NEW_CAR,
            (getLicenseFeePriceDetails(price, car?.licenseFeePrices)
              ?.licenseFeePrice ?? 0) + price,
          );

        default:
          return '';
      }
    },
    [
      isCarZeroKm,
      car?.licenseFeePrices,
      car?.originalLeaseCarPrice,
      formatString,
      newCarTooltipDesc,
      price,
      primeRate,
    ],
  );

  const carCardNextUrl = useCarPageUrl(
    car,
    isPrivateLeasingInWishPage ? CAR_FLOW_TYPE.LEASING_CAR : selectedButton,
  );

  const subscriptionCardNextUrl = useCarSubscriptionPageUrl(
    car,
    selectedButton,
  );

  const getCarFlowTypeUrl = useMemo(() => {
    if (car.acquireType === CarAcquireType.SUBSCRIPTION) {
      return subscriptionCardNextUrl;
    }

    return carCardNextUrl;
  }, [car.acquireType, carCardNextUrl, subscriptionCardNextUrl]);

  const fullCarName = useMemo(() => {
    return `${car.manufacturerDisplayName || car.dmvManufacturerName} ${
      car.modelNameDisplayName || car.dmvModelName
    }`;
  }, [
    car.dmvManufacturerName,
    car.dmvModelName,
    car.manufacturerDisplayName,
    car.modelNameDisplayName,
  ]);

  const leadEventDetails = useLeadEventDetails(leadLocationOption, car);
  const onClickLeaveDetails = useCallback(() => {
    dispatch(setCarType(selectedButton));

    events.sendEvent(
      {
        action: EActionEvent.CAR_PAGE_LEAD,
        category: ECategoryEvent.LEAD,
      },
      {
        carModel: car?.modelNameDisplayName || car?.dmvModelName,
        carMake: car?.dmvManufacturerName,
        carYear: car?.year,
        ...leadEventDetails,
        dealType: selectedButton,
        gaLabel: shouldShowFomo,
      },
    );

    setSelectedCar(car);
    openContactUsModal({
      source: isPrivateLeasing
        ? ESourceEventOptions.LEASE_CAR_RESULT_PAGE
        : ESourceEventOptions.NEW_CAR_RESULT_PAGE,
      flow: EContactUsFlow.LEAVE_DETAILS,
      redirectTo: CONTACT_US_REDIRECTION.RESULTS_PAGE,
      campaignNumber: getCampaignNumberByCar(car, selectedButton),
      subMedia: subMedia.contactUsCarCard,
      car: car,
      note: getCarDetailsNote(businessLine, {
        manufacturer:
          (car.manufacturerDisplayName || car.carZoneMakeName) ?? '',
        model: car.carZoneModelNameEn ?? '',
        price: `${price}`,
        isCarZeroKm,
      }),
    });
  }, [
    businessLine,
    getCarDetailsNote,
    isCarZeroKm,
    price,
    dispatch,
    selectedButton,
    events,
    car,
    leadEventDetails,
    setSelectedCar,
    openContactUsModal,
    isPrivateLeasing,
    shouldShowFomo,
  ]);

  const selectedPriceButtonLabel = useMemo(
    () =>
      CAR_DEAL_TYPE_OPTIONS.find(
        (dealType) => dealType.value === selectedButton,
      )?.label,
    [selectedButton],
  );

  const parsedCardBullets = useMemo(() => {
    const { carBullets } = car;

    if (!carBullets || carBullets.length === 0) {
      return [];
    }
    if (carBullets.length <= 2) {
      return carBullets;
    }

    const seed = generateSeed(`${car.id}`, new Date());
    const randomIndices = getRandomIndices(carBullets.length, 2, seed);
    return randomIndices.map((index) => carBullets[index]);
  }, [car]);

  const leaveDetailsLabel = useMemo(() => {
    return RESOURCES.CAR_FLOW_SUMMARY_CONTACT_ME;
  }, []);

  const carPromotionDetails = useMemo(() => {
    if (
      car.promotion &&
      car.promotion.isPublish &&
      !isValueInCookie('carVisited', car.id) &&
      !isWishListPage
    ) {
      return car.promotion;
    }
  }, [car.promotion, car.id, isWishListPage]);

  const promoLongModelName = useMemo(() => {
    return (car.modelNameDisplayName || car.dmvModelName).length > 7;
  }, [car.modelNameDisplayName, car.dmvModelName]);

  const specialPromotionLabel = useMemo(() => {
    const prioritizedItem = car?.carLabels?.data.find(
      (label) => label.isPrioritized || label.type === 'promotion',
    );

    if (prioritizedItem) return prioritizedItem.title;

    return null;
  }, [car]);

  return (
    <Box sx={classes.root}>
      <Box sx={classes.cardContainer}>
        {/* Car Promotion */}
        {carPromotionDetails && carPromotionDetails.isPublish ? (
          <>
            <Box sx={classes.sliderContainer}>
              <CarImagesKitCarousel
                ikThumb={imageKitThumb.carCard}
                images={images}
                customStyle={CarImagesCarouselStyle}
              />
              {(carPromotionDetails.topLabel || topLabel) && (
                <Box sx={classes.bottomLabel}>
                  <CarTopLabel
                    title={carPromotionDetails.topLabel || topLabel}
                    customTextStyle={topLabelStyles}
                  />
                </Box>
              )}

              <Box sx={classes.wishlistIcon}>
                <WishlistButton
                  onClick={onWishlistIconClicked}
                  isClicked={isInWishlist}
                  carTitle={fullCarName}
                  customStyles={customWishlistStyles}
                />
              </Box>
            </Box>

            <Link
              href={getCarFlowTypeUrl}
              style={promoRoot.container}
              aria-label={`${carPromotionDetails?.title} ${RESOURCES.CAR_SUBMIT_BUTTON2} ${fullCarName}`}
              onClick={onClickSubmitButton}
            >
              {carPromotionDetails.title && (
                <Box>
                  <Typography variant="h3" sx={promoRoot.title}>
                    {parseMarkdownBold(carPromotionDetails.title)}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="h4" sx={promoRoot.brand}>
                  {car.manufacturerDisplayName || car.dmvManufacturerName}
                  <CarPromotionDoodleStar
                    Component={
                      promoLongModelName
                        ? CarPromotionDoodleStarTightStyled
                        : CarPromotionDoodleStarStyled
                    }
                    aria-hidden="true"
                  />
                </Typography>
                <Box>
                  <Typography
                    variant="h4"
                    sx={
                      promoLongModelName ? promoRoot.modelLong : promoRoot.model
                    }
                  >
                    <CarPromotionDoodleAsterisk
                      Component={
                        promoLongModelName
                          ? CarPromotionDoodleAsteriskTightStyled
                          : CarPromotionDoodleAsteriskStyled
                      }
                      aria-hidden="true"
                    />
                    {car.modelNameDisplayName || car.dmvModelName}
                    <CarPromotionDoodleDiamond
                      Component={
                        promoLongModelName
                          ? CarPromotionDoodleDiamondTightStyled
                          : CarPromotionDoodleDiamondStyled
                      }
                      aria-hidden="true"
                    />
                  </Typography>
                </Box>
              </Box>
              <Box>
                {carPromotionDetails.bottomLabel && (
                  <Typography sx={promoRoot.label}>
                    {carPromotionDetails.bottomLabel}
                  </Typography>
                )}
              </Box>
            </Link>
          </>
        ) : (
          // Car Promotion End
          <>
            <Box sx={classes.sliderContainer}>
              <CarImagesKitCarousel
                ikThumb={imageKitThumb.carCard}
                images={images}
                customStyle={CarImagesCarouselStyle}
              />

              {topLabel && (
                <Box sx={classes.bottomLabel}>
                  <CarTopLabel
                    title={topLabel}
                    customTextStyle={topLabelStyles}
                  />
                </Box>
              )}

              <Box sx={classes.wishlistIcon}>
                <WishlistButton
                  onClick={onWishlistIconClicked}
                  isClicked={isInWishlist}
                  carTitle={fullCarName}
                  customStyles={customWishlistStyles}
                />
              </Box>
              {isCarOfTheYear && (
                <Box sx={classes.carOfTheYearIcon}>
                  <Image
                    src={carOfTheYear.src}
                    alt="רכב השנה"
                    width={100}
                    height={100}
                  />
                </Box>
              )}
            </Box>
            {/* Car Details */}
            <Link
              href={getCarFlowTypeUrl}
              style={hrefBtnStyle}
              aria-label={`${RESOURCES.CAR_SUBMIT_BUTTON2} ${fullCarName}`}
              onClick={onClickSubmitButton}
            >
              <Box sx={bottomContainer}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  {isPromoted && isInRangeDate && isTimerDisplayed && (
                    <Box
                      sx={{
                        ...frameCampaign,
                      }}
                    >
                      <Frame
                        text={
                          <>
                            <Box component="span" sx={{ ...timerStyle }}>
                              {timeLeft?.days
                                ? `${timeLeft.days} ימים`
                                : `${timeLeft?.hours}:${timeLeft?.minutes}:${timeLeft?.seconds}`}
                            </Box>

                            <Box component="span" sx={{ ...timerText }}>
                              {RESOURCES.END_SALE}
                            </Box>
                          </>
                        }
                        icon={<TimerIcon />}
                      />
                    </Box>
                  )}
                </Box>

                {/* CONTENT */}
                <Box
                  sx={{
                    ...contentContainer,
                    marginBottom: isCarZeroKm ? '45px' : '15px',
                  }}
                >
                  <Typography
                    tabIndex={0}
                    variant="h3"
                    sx={{ ...brandNameStyle, ...classes.carTitle }}
                  >
                    {car.manufacturerDisplayName || car.dmvManufacturerName}
                    {isCarZeroKm && ` ${RESOURCES.ZERO_KM}`}

                    <Box component={'span'} sx={modelNameStyle}>
                      {car.modelNameDisplayName || car.dmvModelName}
                    </Box>
                  </Typography>
                  <FomoLabel
                    pageViews={car.pageViewsOneMonthAgo}
                    viewThreshold={700}
                    fomoContainerStyle={classes.fomoContainer}
                    label={RESOURCES.FOMO_LEASE_PAGE}
                  />

                  {parsedCardBullets.map((cardBullet, i) => {
                    const { resultsPageTitle } = cardBullet;
                    return (
                      <Box
                        sx={bulletsContainer}
                        key={`${resultsPageTitle}-${i}`}
                      >
                        <BulletCard Component={StyledBulletIcon} />
                        <Typography sx={bulletTextStyles}>
                          {resultsPageTitle}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                {isCarZeroKm && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: '0',
                      top: '49%',
                      transform:
                        parsedCardBullets.length === 0
                          ? 'translate(6%, 350%)'
                          : parsedCardBullets.length === 1
                          ? 'translate(6%, 320%)'
                          : 'translate(6%, 380%)',
                    }}
                  >
                    <Frame
                      text={
                        <>
                          {RESOURCES.BEFORE_DISCOUNT1}
                          <Typography
                            component="span"
                            sx={{ fontWeight: 800, fontSize: '14px' }}
                          >
                            {RESOURCES.BEFORE_DISCOUNT2}
                          </Typography>
                        </>
                      }
                      icon={<ZeroKmIcon />}
                      color={`${COLORS.yellow.light1}`}
                    />
                  </Box>
                )}

                {promotionIconUrl && campaign?.icon && (
                  <Box
                    sx={{
                      ...iconCampaign,
                      backgroundImage: `url(${campaign.icon})`,
                    }}
                  ></Box>
                )}
              </Box>
              {isCarZeroKm && (
                <Box sx={zeroKmStyle}>
                  <ZeroKMBackground />
                </Box>
              )}
            </Link>
            {/* FOMO */}

            {/* PRICE */}

            <Box sx={priceStyles}>
              {isPromoted && isInRangeDate && (
                <Box sx={beforeDiscount}>
                  <Typography sx={priceText}>
                    {RESOURCES.PRICE}{' '}
                    <Typography component="span" sx={beforeDiscountText}>
                      {RESOURCES.BEFORE_DISCOUNT}
                    </Typography>
                  </Typography>
                </Box>
              )}
              <PriceContentContainer>
                {specialPromotionLabel && (
                  <></>
                  // TODO: add promotion label with design
                  // <PromotionLabel>{specialPromotionLabel}</PromotionLabel>
                )}
                <Box
                  ref={priceRef}
                  sx={{
                    ...classes.priceWrapper,
                    ...(parsedCardBullets.length === 0 && isCarZeroKm
                      ? { marginTop: '42px' }
                      : {}),
                  }}
                >
                  <Typography sx={priceStyle}>
                    {formatPrice(price)}

                    {(isPrivateLeasing || isPrivateLeasingInWishPage) && (
                      <Typography component="span" fontSize={FONT_SIZES['s']}>
                        {RESOURCES.CAR_PURCHASE_TIME_PERIOD}
                      </Typography>
                    )}

                    <Box
                      component="span"
                      sx={{
                        ...yellowLineContainerStyles,
                        ...(isPrivateLeasing || isPrivateLeasingInWishPage
                          ? yellowLineLeaseContainerStyles
                          : null),
                      }}
                    >
                      <YellowLineTitle style={yellowLineStyles} />
                    </Box>
                  </Typography>

                  <TooltipInfo
                    buttonStyle={tooltipCustomStyles}
                    infoText={legalInfo(
                      isPrivateLeasing || isPrivateLeasingInWishPage
                        ? CAR_FLOW_TYPE.LEASING_CAR
                        : CAR_FLOW_TYPE.NEW_CAR,
                    )}
                    ariaLabel={`${ARIA_LABELS_RESOURCES.CAR_CARD_TOOLTIP_MORE_INFO} ${selectedPriceButtonLabel}`}
                    // ariaLabel={legalInfo(
                    //   isPrivateLeasing || isPrivateLeasingInWishPage
                    //     ? CAR_FLOW_TYPE.LEASING_CAR
                    //     : CAR_FLOW_TYPE.NEW_CAR,
                    // )}
                  />
                </Box>
              </PriceContentContainer>

              {/* bottom price */}
              {!isCarZeroKm &&
                (isPrivateLeasing ||
                (isPrivateLeasingInWishPage && car.leaseMinPriceList > 0) ? (
                  <Typography sx={priceListCarCardStyle}>
                    {RESOURCES.LEASING_PRICE_LIST}
                    {formatPrice(car.leaseMinPriceList)}
                  </Typography>
                ) : (
                  <Box sx={bottomPriceContainer}>
                    <Typography sx={priceListCarCardStyle}>
                      {`${RESOURCES.CAR_PURCHASE_OR} ${formatPrice(
                        car?.newCarLoanMinPrice,
                      )} ${RESOURCES.CAR_PURCHASE_TIME_PERIOD}`}
                    </Typography>
                    {!isCarZeroKm && (
                      <TooltipInfo
                        buttonStyle={tooltipCustomStyles}
                        infoText={legalInfo(CAR_FLOW_TYPE.LOAN_CAR)}
                        ariaLabel={`${legalInfo(
                          CAR_FLOW_TYPE.LOAN_CAR,
                        )} ${selectedPriceButtonLabel}`}
                        placement="bottom"
                      />
                    )}
                  </Box>
                ))}
            </Box>
          </>
        )}

        {/* BUTTONS */}
        <Box sx={classes.btnsDivStyle}>
          <Button
            label={leaveDetailsLabel}
            size={BUTTON_SIZES.LARGE}
            type={BUTTON_TYPES.SECONDARY}
            customStyle={contactUsButton}
            typographyStyle={contactUsButtonText}
            onClickFunc={onClickLeaveDetails}
          />
          <Link
            href={getCarFlowTypeUrl}
            style={hrefBtnStyle}
            aria-label={`${RESOURCES.CAR_SUBMIT_BUTTON2} ${fullCarName}`}
            className="car-next-flow"
          >
            <Button
              onClickFunc={onClickSubmitButton}
              label={RESOURCES.CAR_SUBMIT_BUTTON2}
              ariaLabel={`${RESOURCES.CAR_SUBMIT_BUTTON2} ${fullCarName}`}
              customStyle={buttonStyle}
              type={BUTTON_TYPES.MAIN}
              size={BUTTON_SIZES.LARGE}
              roleVar="presentation"
              tabbable={false}
            />
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default CarCard;
